import { useTranslations } from "next-intl";
import HeartIcon from "@/assets/icons/heart.svg";
import { Rating } from "@/components/common/rating";
import { Img } from "@/components/image";
import { Button } from "@/components/ui/button";
import { DietMenuModal } from "@/features/diets/components/diet-menu-modal/diet-menu-modal";
import type { Globals } from "@/features/globals/types/globals.type";
import { formatDietName } from "@/lib/helpers/format-brand-name";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { DietCatalog } from "../types";
import { AddDietToCart } from "./diet-add-to-cart";
import { DietDetailsDrawer } from "./diet-details-drawer";

type CatalogListItemProps = {
  diet: DietCatalog;
  params?: Record<string, string | string[] | number>;
  elements?: {
    heart?: boolean;
    dietType?: boolean;
  };
  globals?: Globals | null;
  index?: number;
};

const defaultElements = {
  heart: true,
  true: true,
};

export const CatalogListItemSmall = ({
  diet,
  elements,
  globals,
  index,
}: CatalogListItemProps) => {
  const t = useTranslations("diets");
  const configElements = { ...defaultElements, ...elements };
  const dietName = formatDietName(diet.name);

  return (
    <div className="flex flex-col">
      <figure className="relative">
        <DietDetailsDrawer
          configuration={{ configuration: diet.configurationId || "" }}
          dietId={diet.id}
          label={
            <div className="relative block h-[160px] w-full md:w-[200px]">
              <Img
                alt={dietName}
                className={cn(
                  "rounded-tl-lg rounded-tr-lg object-cover md:rounded-tr-none md:rounded-bl-lg",
                )}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                src={diet.imageUrl}
              />
              {globals?.ratingDietListing && (
                <div className="absolute bottom-2 left-2">
                  <Rating
                    count={diet.rating.count}
                    details={false}
                    isTrending={diet.rating.isTrending}
                    productId={diet.id}
                    rating={diet.rating.value}
                    size="md"
                    trendingClassName="ml-1.5"
                  />
                </div>
              )}
            </div>
          }
          trigger={{
            className: "w-full md:w-[200px] block",
          }}
        />

        {configElements.heart && (
          <Button
            className="group absolute top-2 left-2 h-auto min-h-0 rounded-full bg-white px-2 py-2 hover:text-red-700"
            variant="ghost"
          >
            <HeartIcon className="group-hover:fill-red-700" />
          </Button>
        )}
        {configElements.dietType && (
          <p className="absolute bottom-2 left-2 rounded-full bg-white px-2 py-2 font-medium text-xs">
            {/* TODO: Diet type when available */}
          </p>
        )}
      </figure>
      <div className="flex grow flex-col items-start rounded-b-lg border border-brand-grey2 border-t-0 p-4 md:border-none md:p-0">
        <DietDetailsDrawer
          configuration={{ configuration: diet.configurationId || "" }}
          dietId={diet.id}
          label={
            <h4 className="line-clamp-2 text-left font-medium md:mt-2">
              {dietName}
            </h4>
          }
          trigger={{ className: "items-start" }}
        />
        <p className="mt-auto py-2 text-sm">
          <span className="mr-1">{t("from")}</span>
          <strong className="-mt-1 mr-1 inline-block font-bold leading-6">
            {getLocalePrice({ price: parseFloat(diet.basePriceNumeric) })}
          </strong>
          <span>/ {t("day")}</span>
        </p>

        <div className="mt-6 flex h-12 w-full gap-2 border-primary text-base md:hidden">
          <div className="flex-1">
            <DietMenuModal
              dietId={diet.id}
              trigger={
                <Button
                  className="h-12 min-h-0 w-full rounded-lg border-brand-green2-300 px-0 font-medium text-brand-green2-300 md:h-10 md:px-5 xl:h-12"
                  variant="outline"
                >
                  {t("check_menu")}
                </Button>
              }
            />
          </div>
          <div className="flex-1">
            <AddDietToCart
              className="mt-0 h-full w-full px-2 md:px-5"
              configuration={{ configuration: diet.configurationId || "" }}
              diet={diet}
              dietSlug={diet.slug}
              index={index}
            />
          </div>
        </div>

        <AddDietToCart
          button={{
            variant: "outline",
            className:
              "border-primary hidden h-12 text-base md:flex w-auto px-5",
            icon: undefined,
          }}
          className="hidden h-12 w-auto border-primary px-5 text-base md:flex"
          configuration={{ configuration: diet.configurationId || "" }}
          diet={diet}
          dietSlug={diet.slug}
          index={index}
        />
      </div>
    </div>
  );
};
